import piexif from 'piexifjs';

/**
 * Creates a cropped image from the source image
 * @param {string} imageSrc - Source image URL
 * @param {Object} pixelCrop - Crop area in pixels
 * @param {number} rotation - Rotation angle in degrees
 * @param {string} format - Original image format (e.g., 'image/png', 'image/jpeg')
 * @param {number} quality - Image quality for lossy formats (0-1, default: 0.9)
 * @returns {Promise<string>} - Data URL of cropped image
 */
export const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0, format = 'image/jpeg', quality = 0.9) => {
  console.log("🟠 getCroppedImg - Starting with:", {
    imageSrc: imageSrc ? imageSrc.substring(0, 100) + "..." : "null",
    pixelCrop,
    rotation
  });

  try {
    // If the image is already a data URL, use it directly
    if (imageSrc.startsWith('data:')) {
      return await processImageDirect(imageSrc, pixelCrop, rotation, format, quality);
    }

    // For external URLs (like Firebase storage), try to fetch as blob first to avoid CORS
    try {
      console.log("🟠 getCroppedImg - Attempting to fetch image as blob to avoid CORS...");
      const response = await fetch(imageSrc);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      console.log("🟠 getCroppedImg - Successfully created blob URL");
      const result = await processImageDirect(blobUrl, pixelCrop, rotation, format, quality);
      
      // Clean up blob URL
      URL.revokeObjectURL(blobUrl);
      return result;
    } catch (fetchError) {
      console.warn("🟠 getCroppedImg - Fetch failed, trying direct image load:", fetchError);
      // Fallback to direct image loading (may fail due to CORS)
      return await processImageDirect(imageSrc, pixelCrop, rotation, format, quality);
    }
  } catch (error) {
    console.error("🟠 getCroppedImg - All methods failed:", error);
    throw error;
  }
};

const processImageDirect = (imageSrc, pixelCrop, rotation = 0, format = 'image/jpeg', quality = 0.9) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    
    // Only set crossOrigin for external URLs, not for blob URLs or data URLs
    if (!imageSrc.startsWith('blob:') && !imageSrc.startsWith('data:')) {
      image.crossOrigin = 'anonymous';
    }
    
    image.onload = () => {
      try {
        console.log("🟠 processImageDirect - Image loaded:", {
          width: image.width,
          height: image.height
        });
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const maxSize = Math.max(image.width, image.height);
        const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

        // Set canvas size to safe area
        canvas.width = safeArea;
        canvas.height = safeArea;

        // Translate canvas context to center
        ctx.translate(safeArea / 2, safeArea / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-safeArea / 2, -safeArea / 2);

        // Draw rotated image
        ctx.drawImage(
          image,
          safeArea / 2 - image.width * 0.5,
          safeArea / 2 - image.height * 0.5
        );

        const data = ctx.getImageData(0, 0, safeArea, safeArea);

        // Validate crop dimensions
        const cropWidth = Math.max(1, Math.round(pixelCrop.width));
        const cropHeight = Math.max(1, Math.round(pixelCrop.height));
        
        console.log("🟠 processImageDirect - Validated crop dimensions:", {
          original: { width: pixelCrop.width, height: pixelCrop.height },
          validated: { width: cropWidth, height: cropHeight }
        });

        // Set canvas size to final desired crop size
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Paste generated rotate image at the top left corner
        ctx.putImageData(
          data,
          Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
          Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
        );

        // Convert to data URL using the original format
        // For PNG, don't pass quality parameter as it's ignored
        const dataUrl = format === 'image/png' 
          ? canvas.toDataURL(format)
          : canvas.toDataURL(format, quality);
        
        console.log("🟠 processImageDirect - Generated data URL:", {
          format,
          quality: format === 'image/png' ? 'N/A (lossless)' : quality,
          length: dataUrl.length,
          starts: dataUrl.substring(0, 50),
          isValid: dataUrl.startsWith('data:image/')
        });
        
        resolve(dataUrl);
      } catch (canvasError) {
        console.error("🟠 processImageDirect - Canvas processing error:", canvasError);
        reject(canvasError);
      }
    };

    image.onerror = (error) => {
      console.error("🟠 processImageDirect - Image load error:", error);
      reject(new Error(`Failed to load image: ${error.message || 'Unknown error'}`));
    };
    
    image.src = imageSrc;
  });
};

/**
 * Apply image filters and adjustments using Canvas API
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {HTMLImageElement} img - Image element
 * @param {Object} options - Filter options
 */
export const applyImageFilters = (ctx, img, options = {}) => {
  const { brightness = 0, contrast = 0, filter = 'none' } = options;

  // Clear canvas
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Apply brightness and contrast adjustments
  if (brightness !== 0 || contrast !== 0) {
    const brightnessValue = brightness / 100;
    const contrastValue = (contrast + 100) / 100;
    
    ctx.filter = `brightness(${1 + brightnessValue}) contrast(${contrastValue})`;
  }

  // Apply color filters
  switch (filter) {
    case 'grayscale':
      ctx.filter += ' grayscale(100%)';
      break;
    case 'sepia':
      ctx.filter += ' sepia(100%)';
      break;
    case 'vintage':
      ctx.filter += ' sepia(50%) contrast(1.2) brightness(1.1)';
      break;
    case 'highContrast':
      ctx.filter += ' contrast(1.5) brightness(1.1)';
      break;
    case 'warm':
      ctx.filter += ' sepia(20%) saturate(1.2) hue-rotate(10deg)';
      break;
    case 'cool':
      ctx.filter += ' saturate(1.1) hue-rotate(-10deg) brightness(1.05)';
      break;
    default:
      // 'none' - no additional filter
      break;
  }

  // Draw the image with applied filters
  ctx.drawImage(img, 0, 0);

  // Reset filter for future operations
  ctx.filter = 'none';
};

/**
 * Embed EXIF data into a processed image
 * @param {string} imageDataUrl - Processed image data URL
 * @param {Object} exifData - Original EXIF data
 * @returns {Promise<string>} - Image data URL with embedded EXIF
 */
export const embedExifData = async (imageDataUrl, exifData) => {
  console.log("🟢 embedExifData - Starting with:", {
    imageDataUrl: imageDataUrl ? imageDataUrl.substring(0, 50) + "..." : "null",
    imageLength: imageDataUrl ? imageDataUrl.length : 0,
    exifData: exifData ? "present" : "null",
    isJpeg: imageDataUrl ? imageDataUrl.startsWith('data:image/jpeg') : false
  });
  
  try {
    // Check if the image is JPEG format
    if (!imageDataUrl.startsWith('data:image/jpeg')) {
      console.warn('🟢 embedExifData - EXIF embedding only works with JPEG images. Returning original image.');
      return imageDataUrl;
    }

    // Convert data URL to binary string
    const base64Data = imageDataUrl.split(',')[1];
    if (!base64Data) {
      throw new Error('Invalid data URL format');
    }
    
    console.log("🟢 embedExifData - Base64 data extracted:", {
      length: base64Data.length,
      starts: base64Data.substring(0, 20)
    });

    // Create EXIF data object for piexif
    const exifObj = {
      '0th': {},
      'Exif': {},
      'GPS': {},
      '1st': {},
      'thumbnail': null
    };

    // Map common EXIF fields with proper string handling
    if (exifData.Make) exifObj['0th'][piexif.ImageIFD.Make] = String(exifData.Make);
    if (exifData.Model) exifObj['0th'][piexif.ImageIFD.Model] = String(exifData.Model);
    if (exifData.DateTime) exifObj['0th'][piexif.ImageIFD.DateTime] = String(exifData.DateTime);
    if (exifData.Software) exifObj['0th'][piexif.ImageIFD.Software] = String(exifData.Software);
    if (exifData.Artist) exifObj['0th'][piexif.ImageIFD.Artist] = String(exifData.Artist);
    if (exifData.Copyright) exifObj['0th'][piexif.ImageIFD.Copyright] = String(exifData.Copyright);

    // Camera settings
    if (exifData.DateTimeOriginal) exifObj['Exif'][piexif.ExifIFD.DateTimeOriginal] = String(exifData.DateTimeOriginal);
    if (exifData.DateTimeDigitized) exifObj['Exif'][piexif.ExifIFD.DateTimeDigitized] = String(exifData.DateTimeDigitized);

    // GPS data (most important for your use case)
    if (exifData.latitude && exifData.longitude) {
      const lat = parseFloat(exifData.latitude);
      const lon = parseFloat(exifData.longitude);
      
      if (!isNaN(lat) && !isNaN(lon)) {
        // Convert decimal degrees to degrees, minutes, seconds
        const latDMS = decimalToDMS(Math.abs(lat));
        const lonDMS = decimalToDMS(Math.abs(lon));
        
        exifObj['GPS'][piexif.GPSIFD.GPSLatitudeRef] = lat >= 0 ? 'N' : 'S';
        exifObj['GPS'][piexif.GPSIFD.GPSLatitude] = latDMS;
        exifObj['GPS'][piexif.GPSIFD.GPSLongitudeRef] = lon >= 0 ? 'E' : 'W';
        exifObj['GPS'][piexif.GPSIFD.GPSLongitude] = lonDMS;
        
        console.log("🟢 embedExifData - GPS data processed:", { lat, lon });
      }
    }

    if (exifData.GPSAltitude && !isNaN(parseFloat(exifData.GPSAltitude))) {
      const altitude = parseFloat(exifData.GPSAltitude);
      exifObj['GPS'][piexif.GPSIFD.GPSAltitude] = [Math.abs(altitude), 1];
      exifObj['GPS'][piexif.GPSIFD.GPSAltitudeRef] = altitude >= 0 ? 0 : 1;
    }

    console.log("🟢 embedExifData - EXIF object created, attempting dump...");
    
    // Generate EXIF binary data
    const exifBytes = piexif.dump(exifObj);
    console.log("🟢 embedExifData - EXIF bytes generated:", exifBytes.length);
    
    // Insert EXIF data into JPEG
    const newImageData = piexif.insert(exifBytes, base64Data);
    console.log("🟢 embedExifData - EXIF inserted, new data length:", newImageData.length);
    
    // Convert back to data URL
    const newDataUrl = 'data:image/jpeg;base64,' + newImageData;
    console.log("🟢 embedExifData - Final data URL:", {
      length: newDataUrl.length,
      starts: newDataUrl.substring(0, 50),
      isValid: newDataUrl.startsWith('data:image/jpeg')
    });
    
    return newDataUrl;
  } catch (error) {
    console.error('🟢 embedExifData - Error embedding EXIF data:', error);
    console.log("🟢 embedExifData - Returning original image due to error");
    // Return original image if EXIF embedding fails
    return imageDataUrl;
  }
};

/**
 * Convert decimal degrees to degrees, minutes, seconds format for EXIF
 * @param {number} decimal - Decimal degrees
 * @returns {Array} - [degrees, minutes, seconds] as rational numbers
 */
const decimalToDMS = (decimal) => {
  const degrees = Math.floor(decimal);
  const minutesFloat = (decimal - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutes) * 60;
  
  return [
    [degrees, 1],
    [minutes, 1],
    [Math.round(seconds * 1000), 1000] // Use rational number for precision
  ];
};

/**
 * Create a canvas element with specified dimensions
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {HTMLCanvasElement} - Canvas element
 */
export const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

/**
 * Load an image from URL
 * @param {string} url - Image URL
 * @returns {Promise<HTMLImageElement>} - Loaded image element
 */
export const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Convert canvas to blob
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {string} type - MIME type (default: 'image/jpeg')
 * @param {number} quality - Image quality (0-1, default: 0.9)
 * @returns {Promise<Blob>} - Image blob
 */
export const canvasToBlob = (canvas, type = 'image/jpeg', quality = 0.9) => {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
};

/**
 * Resize image while maintaining aspect ratio
 * @param {string} imageSrc - Source image URL
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {Promise<string>} - Resized image data URL
 */
export const resizeImage = async (imageSrc, maxWidth, maxHeight) => {
  const img = await loadImage(imageSrc);
  
  // Calculate new dimensions
  let { width, height } = img;
  
  if (width > height) {
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
  } else {
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }
  }
  
  // Create canvas and draw resized image
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  
  return canvas.toDataURL('image/jpeg', 0.9);
};
